import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiService';
import { formatLocalDateTime } from '../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Message as MessageIcon,
  Terminal as CommandIcon,
  Battery20 as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
  Schedule as ScheduleIcon,
  Pause as PauseIcon,
  PlayCircle as PlayCircleIcon
} from '@mui/icons-material';

// Interfaces
interface TestSession {
  id: number;
  sensor_id: number;
  status: 'active' | 'stopped' | 'expired';
  started_by: number;
  test_topic: string;
  messages_received: number;
  last_message_at: string | null;
  started_at: string;
  stopped_at: string | null;
  expires_at: string | null;
  sensor_name: string;
  sensor_serial_number: string;
  user_username: string;
}

interface TestMessage {
  id: number;
  test_session_id: number;
  topic: string;
  payload: any;
  message_type: 'info' | 'status' | 'response';
  event_type: string | null;
  battery_level: number | null;
  counters: any;
  received_at: string;
}

interface TestCommand {
  id: number;
  test_session_id: number;
  command: string;
  parameters: any;
  status: 'sent' | 'acknowledged' | 'failed';
  response_received: boolean;
  response_data: any;
  error_message: string | null;
  sent_at: string;
  response_at: string | null;
}

interface Sensor {
  id: number;
  name: string;
  serial_number: string;
  imei: string;
  ccid: string;
  is_active: boolean;
}

const TestDevice: React.FC = () => {
  const { sensorId } = useParams<{ sensorId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // State
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [testSession, setTestSession] = useState<TestSession | null>(null);
  const [messages, setMessages] = useState<TestMessage[]>([]);
  const [commands, setCommands] = useState<TestCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commandDialogOpen, setCommandDialogOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState('');
  const [sendingCommand, setSendingCommand] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  
  // WebSocket connection
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Auto-refresh for messages
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if user has Ingecom role
  const hasIngecomRole = user?.role === 'ingecom';
  
  // Available commands (loaded from API)
  const [availableCommands, setAvailableCommands] = useState<Array<{value: string, label: string}>>([]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!testSession) return;
    
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected for test device');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'test_message' && data.session_id === testSession.id) {
            setMessages(prev => [data.message, ...prev]);
            setTestSession(prev => prev ? {
              ...prev,
              messages_received: prev.messages_received + 1,
              last_message_at: data.message.received_at
            } : null);
          } else if (data.type === 'test_session_update' && data.session_id === testSession.id) {
            setTestSession(prev => prev ? {
              ...prev,
              status: data.status,
              messages_received: data.messages_received,
              last_message_at: data.last_message_at
            } : null);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
    }
  }, [testSession]);

  // Fetch sensor data
  const fetchSensor = useCallback(async () => {
    if (!sensorId) return;
    
    try {
      const response = await apiClient.get(`/sensors/${sensorId}`);
      setSensor(response.data);
    } catch (err: any) {
      console.error('Error fetching sensor:', err);
      setError(t('common.failedToLoad', { resource: t('navigation.sensors') }));
    }
  }, [sensorId, t]);

  // Fetch active test session
  const fetchTestSession = useCallback(async () => {
    if (!sensorId) return;
    
    try {
      const response = await apiClient.get(`/test/sessions?sensor_id=${sensorId}&status_filter=active`);
      if (response.data.items.length > 0) {
        setTestSession(response.data.items[0]);
      } else {
        setTestSession(null);
      }
    } catch (err: any) {
      console.error('Error fetching test session:', err);
      // Don't set error here as it's normal to not have an active session
    }
  }, [sensorId]);

  // Fetch test messages
  const fetchMessages = useCallback(async () => {
    if (!testSession) return;
    
    try {
      const response = await apiClient.get(`/test/${testSession.id}/messages?limit=50`);
      setMessages(response.data.items);
    } catch (err: any) {
      console.error('Error fetching messages:', err);
    }
  }, [testSession]);

  // Fetch test commands
  const fetchCommands = useCallback(async () => {
    if (!testSession) return;
    
    try {
      const response = await apiClient.get(`/test/${testSession.id}/commands?limit=20`);
      setCommands(response.data.items);
    } catch (err: any) {
      console.error('Error fetching commands:', err);
    }
  }, [testSession]);

  // Fetch available commands
  const fetchAvailableCommands = useCallback(async () => {
    try {
      const response = await apiClient.get('/test/commands/available');
      const commands = Object.entries(response.data).map(([value, description]) => {
        const translationKey = `testDevice.commands.${value}`;
        const translatedLabel = t(translationKey);
        // Check if translation exists (if it returns the key itself, translation doesn't exist)
        const label = translatedLabel !== translationKey ? translatedLabel : description as string;
        return { value, label };
      });
      setAvailableCommands(commands);
    } catch (err: any) {
      console.error('Error fetching available commands:', err);
      // Fallback to default commands if API fails
      const fallbackCommands = [
        { value: 'clearVibrationCounters', label: t('testDevice.commands.clearVibrationCounters') },
        { value: 'startTestMode', label: t('testDevice.commands.startTestMode') },
        { value: 'stopTestMode', label: t('testDevice.commands.stopTestMode') },
        { value: 'requestGpsLocation', label: t('testDevice.commands.requestGpsLocation') },
        { value: 'stopRequestGpsLocation', label: t('testDevice.commands.stopRequestGpsLocation') },
        { value: 'resetCounters', label: t('testDevice.commands.resetCounters') },
        { value: 'turnOff', label: t('testDevice.commands.turnOff') },
        { value: 'fwUpdate', label: t('testDevice.commands.fwUpdate') },
        { value: 'forceFwUpdate', label: t('testDevice.commands.forceFwUpdate') }
      ];
      setAvailableCommands(fallbackCommands);
    }
  }, [t]);

  // Auto-refresh messages function
  const startAutoRefresh = useCallback(() => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    
    if (testSession && testSession.status === 'active' && autoRefreshEnabled) {
      autoRefreshIntervalRef.current = setInterval(() => {
        fetchMessages();
      }, 5000); // Refresh every 5 seconds
    }
  }, [testSession, autoRefreshEnabled, fetchMessages]);

  // Stop auto-refresh
  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
  }, []);

  // Start test mode
  const startTestMode = async () => {
    if (!sensorId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.post('/test/start', {
        sensor_id: parseInt(sensorId)
      });
      setTestSession(response.data);
      setMessages([]);
      setCommands([]);
    } catch (err: any) {
      console.error('Error starting test mode:', err);
      setError(err.response?.data?.detail || t('testDevice.errors.failedToStart'));
    } finally {
      setLoading(false);
    }
  };

  // Stop test mode
  const stopTestMode = async () => {
    if (!testSession) return;
    
    try {
      setLoading(true);
      await apiClient.post(`/test/${testSession.id}/stop`);
      setTestSession(prev => prev ? { ...prev, status: 'stopped' } : null);
    } catch (err: any) {
      console.error('Error stopping test mode:', err);
      setError(err.response?.data?.detail || t('testDevice.errors.failedToStop'));
    } finally {
      setLoading(false);
    }
  };

  // Send command
  const sendCommand = async () => {
    if (!testSession || !selectedCommand) return;
    
    try {
      setSendingCommand(true);
      const response = await apiClient.post(`/test/${testSession.id}/command`, {
        command: selectedCommand
      });
      setCommands(prev => [response.data, ...prev]);
      setCommandDialogOpen(false);
      setSelectedCommand('');
    } catch (err: any) {
      console.error('Error sending command:', err);
      setError(err.response?.data?.detail || t('testDevice.errors.failedToSendCommand'));
    } finally {
      setSendingCommand(false);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSensor(), fetchTestSession(), fetchAvailableCommands()]);
      setLoading(false);
    };
    
    loadData();
  }, [fetchSensor, fetchTestSession, fetchAvailableCommands]);

  // Load messages and commands when test session changes
  useEffect(() => {
    if (testSession) {
      fetchMessages();
      fetchCommands();
    }
  }, [testSession, fetchMessages, fetchCommands]);

  // Auto-refresh effect
  useEffect(() => {
    if (testSession && testSession.status === 'active' && autoRefreshEnabled) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
    
    return () => {
      stopAutoRefresh();
    };
  }, [testSession, autoRefreshEnabled, startAutoRefresh, stopAutoRefresh]);

  // WebSocket connection
  useEffect(() => {
    if (testSession && testSession.status === 'active') {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopAutoRefresh();
    };
  }, [testSession, connectWebSocket]);

  // Check permissions
  if (!hasIngecomRole) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('testDevice.errors.insufficientPermissions')}
        </Alert>
      </Box>
    );
  }

  if (loading && !sensor) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !sensor) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'stopped': return 'default';
      case 'expired': return 'warning';
      default: return 'default';
    }
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return <MessageIcon color="info" />;
      case 'response': return <CommandIcon color="success" />;
      default: return <SignalIcon color="primary" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/sensors')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">
          {t('testDevice.title')} - {sensor?.name || sensor?.serial_number}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sensor Info */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('testDevice.sensorInfo')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {t('sensors.name')}
            </Typography>
            <Typography variant="body1">{sensor?.name}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {t('sensor.fields.serialNumber')}
            </Typography>
            <Typography variant="body1">{sensor?.serial_number}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {t('common.imei')}
            </Typography>
            <Typography variant="body1">{sensor?.imei}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {t('common.status')}
            </Typography>
            <Chip 
              label={sensor?.is_active ? t('common.active') : t('common.inactive')}
              color={sensor?.is_active ? 'success' : 'error'}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Test Session Control */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {t('testDevice.testSession')}
          </Typography>
          <Box>
            {testSession && testSession.status === 'active' ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setCommandDialogOpen(true)}
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  {t('testDevice.sendCommand')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={stopTestMode}
                  disabled={loading}
                >
                  {t('testDevice.stopTest')}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<StartIcon />}
                onClick={startTestMode}
                disabled={loading || !sensor?.is_active}
              >
                {t('testDevice.startTest')}
              </Button>
            )}
          </Box>
        </Box>

        {testSession ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {t('common.status')}
              </Typography>
              <Chip 
                label={t(`testDevice.status.${testSession.status}`)}
                color={getStatusColor(testSession.status)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {t('testDevice.messagesReceived')}
              </Typography>
              <Typography variant="body1">
                <Badge badgeContent={testSession.messages_received} color="primary">
                  <MessageIcon />
                </Badge>
                {testSession.messages_received}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {t('testDevice.startedAt')}
              </Typography>
              <Typography variant="body1">
                {formatLocalDateTime(testSession.started_at)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {t('testDevice.lastMessage')}
              </Typography>
              <Typography variant="body1">
                {testSession.last_message_at 
                  ? formatLocalDateTime(testSession.last_message_at)
                  : t('common.never')
                }
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t('testDevice.noActiveSession')}
          </Typography>
        )}
      </Paper>

      {/* Messages and Commands */}
      {testSession && (
        <Grid container spacing={3}>
          {/* Messages */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('testMode.messages')} ({messages.length})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={autoRefreshEnabled ? t('testDevice.disableAutoRefresh') : t('testDevice.enableAutoRefresh')}>
                    <IconButton
                      onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                      size="small"
                      color={autoRefreshEnabled ? 'primary' : 'default'}
                    >
                      {autoRefreshEnabled ? <PauseIcon /> : <PlayCircleIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.refresh')}>
                    <IconButton onClick={fetchMessages} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {messages.length > 0 ? (
                  <List dense>
                    {messages.map((message) => (
                      <ListItem key={message.id} divider>
                        <ListItemIcon>
                          {getMessageTypeIcon(message.message_type)}
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={message.message_type}
                                size="small"
                                variant="outlined"
                              />
                              {message.event_type && (
                                <Chip
                                  label={message.event_type}
                                  size="small"
                                  color="primary"
                                />
                              )}
                              {message.battery_level && (
                                <Chip
                                  icon={<BatteryIcon />}
                                  label={`${message.battery_level}mV`}
                                  size="small"
                                  color="info"
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {formatLocalDateTime(message.received_at)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" component="div" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre' }}>
                                {JSON.stringify(message.payload, null, 2)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('testMode.noMessages')}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Commands */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('testMode.commandsSent')} ({commands.length})
                </Typography>
                <IconButton onClick={fetchCommands} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {commands.length > 0 ? (
                  <List dense>
                    {commands.map((command) => (
                      <ListItem key={command.id} divider>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {command.command}
                              </Typography>
                              <Chip
                                label={command.status}
                                size="small"
                                color={command.status === 'acknowledged' ? 'success' :
                                       command.status === 'failed' ? 'error' : 'default'}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('testDevice.sentAt')}: {formatLocalDateTime(command.sent_at)}
                              </Typography>
                              {command.response_at && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('testDevice.responseAt')}: {formatLocalDateTime(command.response_at)}
                                </Typography>
                              )}
                              {command.error_message && (
                                <Typography variant="caption" color="error" display="block">
                                  {command.error_message}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('testMode.noCommands')}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Command Dialog */}
      <Dialog open={commandDialogOpen} onClose={() => setCommandDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('testDevice.sendCommand')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('testDevice.selectCommand')}</InputLabel>
            <Select
              value={selectedCommand}
              onChange={(e) => setSelectedCommand(e.target.value)}
              label={t('testDevice.selectCommand')}
            >
              {availableCommands.map((cmd) => (
                <MenuItem key={cmd.value} value={cmd.value}>
                  {cmd.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommandDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={sendCommand} 
            variant="contained"
            disabled={!selectedCommand || sendingCommand}
          >
            {sendingCommand ? <CircularProgress size={20} /> : t('common.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestDevice;